import React from 'react'
import ReactDOM from 'react-dom'
import pino from 'pino'
import * as H from 'history'

import { loadableReady } from '@loadable/component'

import { Provider } from 'react-redux'
import { ThemeProvider, CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { DataProvider } from 'react-ssr-data-loader'

import { DataLoaderState } from 'react-ssr-data-loader'
import { PageLifecycleProvider, PageLifecycleProviderRenderProps } from 'page-lifecycle-provider'
import { WatchtowerBrowserRouter } from 'project-watchtower'

import { setImageManagerPolicy } from 'common/helpers/image'
import { IRouteCache, RouteCache } from 'common/data/route-cache'
import { resources, DataLoaderGlobalParams } from 'common/data/resources'
import {
    handlePageEvents,
    handleDataProviderEvent,
} from 'component-library/__App/page-events-handler'

import { createEventBuffer } from 'component-library/tracking/gtm/event-buffer'
import { createSpaInitialisedEvent } from 'component-library/tracking/gtm/events'
import { initLaunchDarklyClientToggles } from 'common/feature-togglings/ClientABTesting/ab-testing'
import { setGptEnabled } from 'component-library/advertising/gpt/GptAdProvider/GptAdProvider'
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { LoggerContext } from 'component-library/diagnostics/LoggerContext'
import { ConfigurationContext } from 'common/context-providers/configuration-context'
import { IsHydrating } from 'common/context-providers/is-hydrating'
import { AppState, configureStore, Store } from 'common/store'
import { configureTrackJs } from 'common/trackjs'
import { BaseClientConfig } from 'common/client/BaseClientConfig'
import { adBlockEnabled } from 'component-library/advertising/gpt/GptAdProvider/utils'
import { loadFeatures } from 'common/feature-togglings/reducer'
import { PageEventContext } from 'common/context-providers/pageEvent-context'
import { createEventObserver } from 'common/observable'

import { Theme } from 'component-library/__styling/themes'
import { ConsentLevel, useConsentState } from 'common/data/consent/consent.redux'
import { hasConsentLevel } from 'common/data/consent/has-consent-level'
import { isIE } from 'common/client/helpers'
import { DataLayerEventName } from 'common/helpers/DataLayerEventName'
import { GetDcrMeta } from 'component-library/tracking/nielsen/nielsen-meta'
import { AllEvents, AllEventTypes } from 'common/events/all-events'

declare const config: BaseClientConfig
setImageManagerPolicy(config.akamaiImageManagerPolicy)

let routeCacheRef: IRouteCache
declare const INITIAL_STATE: AppState
declare let PAGE_DATA: DataLoaderState | undefined
const initialState = typeof INITIAL_STATE !== 'undefined' ? INITIAL_STATE : undefined
const dataLoaderState = typeof PAGE_DATA !== 'undefined' ? PAGE_DATA : undefined

const cache = createCache()

const log = pino({
    level: 'trace',
})

type AppComponent = React.FC<{
    hostname: string
    protocol: string
    location: H.Location
    onEvent: (event: AllEvents) => void
    services: DataLoaderGlobalParams
}>

export function initClient({
    version,
    applicationInitialised,
    theme,
    requireApplication,
    getDcrMeta,
}: {
    version: { sha: string; buildNumber: string }
    applicationInitialised?: (services: { store: Store; config: BaseClientConfig }) => void
    theme: Theme
    requireApplication: () => AppComponent
    getDcrMeta: GetDcrMeta
}) {
    // Clear state so it can't be picked up by data load
    PAGE_DATA = undefined
    configureTrackJs(version.buildNumber)

    const store = configureStore(config, { initialState, syncState: true })

    // Ads cannot be a/b tested, they are a once off toggle
    setGptEnabled(isFeatureEnabled(store.getState().toggles, 'gpt-ads'))
    if (window.location.search.indexOf('ad_test_debug') !== -1) {
        store.dispatch(loadFeatures({ 'debug-ads': { enabled: true } }))
    }

    const handlePageLifecycleEvents = (event: AllEvents) => {
        // currentPageProps is not available on the types, but contains the
        // current page variables. Because page completed is buffered it contains old
        // page variables, this allows the events to use the current values instead
        handlePageEvents(
            event,
            log,
            store.getState().toggles,
            pageLifecycleProvider.current.currentPageProps,
            config.lotameId,
            config.nielsenSiteName,
            getDcrMeta
        )
    }

    const { subscribe, broadcast } = createEventObserver<AllEventTypes>()

    const pageLifecycleProvider = React.createRef<any>()
    const fireEvent = createEventBuffer<AllEvents>(event => {
        broadcast(event.type)
        return handlePageLifecycleEvents(event)
    })
    let isInitialRender = true

    const render = () =>
        new Promise<void>(resolve => {
            const container = document.getElementById('root')
            let renderFn = ReactDOM.hydrate
            // Add ?debug_ssr_mismatch
            if (
                process.env.NODE_ENV !== 'production' &&
                window.location.search.indexOf('debug_ssr_mismatch') !== -1 &&
                isInitialRender
            ) {
                console.log('Server-Render Result')
                console.log(container!.innerHTML)
                container!.innerHTML = ''
                renderFn = ReactDOM.render
            }

            const currentIsInitialRender = isInitialRender

            const App = requireApplication()
            renderFn(
                <CacheProvider value={cache}>
                    <Provider store={store}>
                        <IsHydrating.Provider value={isInitialRender}>
                            <LoggerContext.Provider value={log}>
                                <ConfigurationContext.Provider value={config}>
                                    <ThemeProvider theme={theme}>
                                        <PageEventContext.Provider value={subscribe}>
                                            <ClientApplication
                                                pageLifecycleProvider={pageLifecycleProvider}
                                                store={store}
                                                theme={theme}
                                                fireEvent={fireEvent}
                                                App={App}
                                            />
                                        </PageEventContext.Provider>
                                    </ThemeProvider>
                                </ConfigurationContext.Provider>
                            </LoggerContext.Provider>
                        </IsHydrating.Provider>
                    </Provider>
                </CacheProvider>,
                container,
                () => {
                    if (
                        process.env.NODE_ENV !== 'production' &&
                        window.location.search.indexOf('debug_ssr_mismatch') !== -1 &&
                        currentIsInitialRender
                    ) {
                        console.log('Client-Render Result')
                        console.log(container!.innerHTML)
                    }
                    resolve()
                }
            )
            isInitialRender = false
        })

    // This tells @loadable/components to preload the loadable components that were included on the page.
    loadableReady()
        // Once that is done, we are safe to hydrate the client!
        .then(() => render())
        .then(() =>
            initLaunchDarklyClientToggles(
                store,
                config.launchDarklyClientId,
                config.launchDarklyClientTimeout
            )
        )
        .then(() => {
            // Once hydrated, we should re-render to kick-off any new client
            // only rendering
            return render()
        })
        .then(() => {
            const toggles = store.getState().toggles
            fireEvent(createSpaInitialisedEvent(toggles))

            // Addition of an adBlockEnabled property to window. This can be used for
            // accessing by scripts such as TrackJS which need to be able to mute
            // errors when an AdBlocker is enabled.
            window.adBlockEnabled = adBlockEnabled()
            // Set the canary cookie which can be used by Akamai to split traffic.
            document.cookie = `C=${toggles.featureState.canary ? 1 : 0};path=/`
        })
        .then(() => {
            if (applicationInitialised) {
                applicationInitialised({ config, store })
            }
        })

    return { render, routeCacheRef }
}

const ClientApplication: React.FC<{
    store: Store
    theme: Theme
    App: AppComponent
    pageLifecycleProvider: React.MutableRefObject<any>
    fireEvent: (event: AllEvents) => void
}> = ({ store, pageLifecycleProvider, fireEvent, App }) => {
    // We need to fire the current consent level event so GTM knows what mode to load in
    const { consentLevel } = useConsentState()
    const consent = consentLevel || ConsentLevel.None

    fireEvent({
        type: DataLayerEventName.cookieConsent,
        originator: 'ClientApplication',
        payload: {
            permissionAdvertising: hasConsentLevel(consent, ConsentLevel.Advertising),
            permissionAnalytics: hasConsentLevel(consent, ConsentLevel.Analytics),
            permissionEssential: hasConsentLevel(consent, ConsentLevel.Essential),
        },
    })

    return (
        <WatchtowerBrowserRouter forceRefresh={isIE(navigator)}>
            <PageLifecycleProvider
                {...{
                    wrappedComponentRef: (ref: any) => (pageLifecycleProvider.current = ref),
                }}
                onEvent={fireEvent}
                render={pageProps => renderPage(pageProps, store, App, fireEvent)}
            />
        </WatchtowerBrowserRouter>
    )
}

function renderPage(
    pageProps: PageLifecycleProviderRenderProps,
    store: Store,
    App: AppComponent,
    fireEvent: (event: AllEvents) => void
) {
    return (
        <RouteCache
            log={log}
            render={routeCache => {
                routeCacheRef = routeCache

                const additionalProps: DataLoaderGlobalParams = {
                    log,
                    routeCache,
                    config,
                    store,
                }

                return (
                    <DataProvider
                        initialState={dataLoaderState}
                        isServerSideRender={false}
                        resources={resources}
                        globalProps={additionalProps}
                        onEvent={handleDataProviderEvent(pageProps, log)}
                    >
                        <App
                            hostname={window.location.host}
                            protocol={window.location.protocol}
                            location={pageProps.currentPageLocation}
                            onEvent={fireEvent}
                            services={additionalProps}
                        />
                    </DataProvider>
                )
            }}
        />
    )
}
